import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useCategories } from '../lib/categories'
import ImportTransactionsModal from '../components/ImportTransactionsModal'
import { useToast } from '../components/ToastContainer'

interface Transaction {
  id: string
  category_id: string
  card_id: string | null
  amount: string
  type: 'income' | 'expense'
  transaction_date: string
  description: string | null
  is_essential: boolean
  created_at: string
  updated_at: string
}

interface TransactionListResponse {
  transactions: Transaction[]
  total: number
  page: number
  limit: number
  pages: number
}

export default function TransactionsPage() {
  const queryClient = useQueryClient()
  const [typeFilter, setTypeFilter] = useState<'all' | 'income' | 'expense'>('all')
  const [page, setPage] = useState(1)
  const limit = 20
  const [isImportModalOpen, setIsImportModalOpen] = useState(false)

  const { data: categoriesData } = useCategories()
  const categories = categoriesData?.categories ?? []

  const { data: listResponse, isLoading, error, refetch } = useQuery<TransactionListResponse>({
    queryKey: ['transactions', typeFilter, page],
    queryFn: async () => {
      const params = new URLSearchParams()
      params.set('page', String(page))
      params.set('limit', String(limit))
      if (typeFilter !== 'all') params.set('type', typeFilter)
      const res = await api.get<TransactionListResponse>(`/transactions/?${params.toString()}`)
      return res.data
    },
  })

  const mutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.delete(`/transactions/${id}`)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['transactions-summary'] })
    },
  })

  const { success, error: toastError } = useToast()

  const list = listResponse?.transactions ?? []
  const totalPages = listResponse?.pages ?? 0
  const totalItems = listResponse?.total ?? 0

  const totalIncome = list
    .filter((t) => t.type === 'income')
    .reduce((acc, t) => {
      const n = parseFloat(t.amount.replace(/[^\d.-]/g, ''))
      return acc + (isNaN(n) ? 0 : n)
    }, 0)
  const totalExpenses = list
    .filter((t) => t.type === 'expense')
    .reduce((acc, t) => {
      const n = parseFloat(t.amount.replace(/[^\d.-]/g, ''))
      return acc + (isNaN(n) ? 0 : n)
    }, 0)

  // Get category name by ID
  const getCategoryName = (categoryId: string) => {
    const cat = categories.find(c => c.id === categoryId)
    return cat?.name || 'Outros'
  }

  // Get category color by ID
  const getCategoryColor = (categoryId: string) => {
    const cat = categories.find(c => c.id === categoryId)
    return cat?.color || '#6C757D'
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-[var(--color-text)]">Transações</h1>

        <div className="flex gap-2">
          <button
            type="button"
            className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm font-medium text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-subtle)] hover:border-[var(--color-border-strong)]"
            onClick={() => refetch()}
          >
            Atualizar
          </button>
          <button
            type="button"
            className="rounded-lg bg-[var(--color-brand-500)] px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--color-brand-600)]"
            onClick={() => setIsImportModalOpen(true)}
          >
            Importar CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap gap-3">
        <input
          type="text"
          value=""
          disabled
          placeholder="Busca não disponível nesta versão da API"
          className="flex-1 min-w-[200px] rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)] placeholder-[var(--color-text-muted)] focus:border-[var(--color-brand-400)] focus:ring-1 focus:ring-[var(--color-brand-400)]"
        />

        <select
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value as typeof typeFilter)
            setPage(1)
          }}
          className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)] focus:border-[var(--color-brand-400)] focus:ring-1 focus:ring-[var(--color-brand-400)]"
        >
          <option value="all">Todos os tipos</option>
          <option value="income">Entradas</option>
          <option value="expense">Saídas</option>
        </select>
      </div>

      {/* Summary */}
      <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SummaryCard label="Total de entradas" value={formatCurrency(totalIncome)} color="success" />
        <SummaryCard label="Total de saídas" value={formatCurrency(totalExpenses)} color="danger" />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-[var(--color-brand-200)] border-t-[var(--color-brand-500)]" />
        </div>
      ) : error ? (
        <div className="p-8 text-center">
          <div className="inline-flex items-center gap-2 text-[var(--color-danger)]">
            <svg xmlns="http://www.w3.org/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
            <span>Erro ao carregar transações</span>
          </div>
        </div>
      ) : list.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface-subtle)] p-8">
          <svg xmlns="http://www.w3.org/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-8 w-8 text-[var(--color-text-muted)]">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 4.5h14.25M3 9h9.75M3 13.5h5.25m5.25-.75H21m-3.75 3.75h3.75m-3.75 2.25h10.5M3 4.5h14.25M3 9h9.75M3 13.5h5.25m5.25-.75H21m-3.75 3.75h3.75M3 4.5h14.25M3 9h9.75" />
          </svg>
          <p className="mt-3 text-[var(--color-text-muted)]">Nenhuma transação encontrada.</p>
        </div>
      ) : (
        <>
          <div className="mt-6 divide-y divide-[var(--color-border)]">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[var(--color-surface-subtle)]">
                  <th className="px-4 py-3 text-left font-medium text-[var(--color-text-muted)]">Descrição</th>
                  <th className="px-4 py-3 text-left font-medium text-[var(--color-text-muted)]">Categoria</th>
                  <th className="px-4 py-3 text-left font-medium text-[var(--color-text-muted)]">Data</th>
                  <th className="px-4 py-3 text-right font-medium text-[var(--color-text-muted)]">Valor</th>
                  <th className="px-4 py-3 text-right font-medium text-[var(--color-text-muted)]">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border)]">
                {list.map((t) => (
                  <tr key={t.id} className="hover:bg-[var(--color-surface-subtle)]">
                    <td className="px-4 py-3">
                      {t.description ? (
                        <span className="text-[var(--color-text)] font-medium">{t.description}</span>
                      ) : (
                        <span className="text-[var(--color-text-muted)]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5">
                        <span 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: getCategoryColor(t.category_id) }}
                        />
                        <span className="text-[var(--color-text)]">{getCategoryName(t.category_id)}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[var(--color-text-muted)]">
                      {formatDate(t.transaction_date)}
                    </td>
                    <td className={`px-4 py-3 text-right font-medium ${t.type === 'income' ? 'text-[var(--color-success)]' : 'text-[var(--color-danger)]'}`}>
                      {t.type === 'income' ? '+' : '-'}{formatCurrency(parseFloat(t.amount.replace(/[^\d.-]/g, '') || '0'))}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => mutation.mutate(t.id)}
                        disabled={mutation.isPending}
                        className="rounded border border-[var(--color-border)] bg-white px-2 py-1 text-xs font-medium text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-danger-light)] hover:border-[var(--color-danger)] hover:text-[var(--color-danger)] disabled:opacity-50"
                      >
                        {mutation.isPending ? 'Excluindo...' : 'Excluir'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-center gap-2">
              <button
                type="button"
                disabled={page <= 1 || isLoading}
                onClick={() => setPage(page - 1)}
                className="rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-sm font-medium text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-subtle)] disabled:opacity-50"
              >
                Anterior
              </button>
              <span className="text-sm text-[var(--color-text-muted)]">
                Página {page} de {totalPages} ({totalItems} itens)
              </span>
              <button
                type="button"
                disabled={page >= totalPages || isLoading}
                onClick={() => setPage(page + 1)}
                className="rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-sm font-medium text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-subtle)] disabled:opacity-50"
              >
                Próximo
              </button>
            </div>
          )}
        </>
      )}

      {/* Import Transactions Modal */}
      <ImportTransactionsModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
      />
    </div>
  )
}

function SummaryCard({ label, value, color }: { label: string; value: string; color: 'success' | 'danger' }) {
  const textColor = color === 'success' ? 'text-[var(--color-success)]' : 'text-[var(--color-danger)]'
  const dotColor = color === 'success' ? 'bg-[var(--color-success)]' : 'bg-[var(--color-danger)]'

  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-sm">
      <div className="flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${dotColor}`} />
        <span className="text-sm font-medium text-[var(--color-text-muted)]">{label}</span>
      </div>
      <p className={`mt-2 text-xl font-bold ${textColor}`}>{value}</p>
    </div>
  )
}

function formatDate(dateStr: string) {
  // dateStr is YYYY-MM-DD
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('pt-BR')
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value)
}

function parseFloat(str: string): number {
  const cleaned = str.replace(/[^\d.-]/g, '')
  const n = parseFloat(cleaned)
  return isNaN(n) ? 0 : n
}
